import type { CstNode, IToken } from 'chevrotain';
import { StardogSparqlParser, isCstNode } from 'millan';

import { STARDOG_LABEL_IRI } from 'bones/constants/iris';
import { ChainableCst } from 'bones/utils/queryBuilder/parsing/helpers/chainableCst';
import { getFullIri } from 'bones/utils/queryBuilder/parsing/helpers/getFullIri';
import { groupMany } from 'bones/utils/queryBuilder/parsing/helpers/groupMany';
import {
  ChainableSparqlCst,
  sparqlTokens,
} from 'bones/utils/queryBuilder/parsing/helpers/sparql';
import { tokenSort } from 'bones/utils/queryBuilder/parsing/helpers/tokenSort';

export const enum SparqlTokenType {
  IRI = 'IRI',
  LITERAL = 'LITERAL',
  VAR = 'VAR',
}

interface BaseSparqlToken {
  type: SparqlTokenType;
}

export interface SparqlIriToken extends BaseSparqlToken {
  iri: IToken;
  type: SparqlTokenType.IRI;
}
export interface SparqlLiteralToken extends BaseSparqlToken {
  literal: IToken;
  langIri?: IToken;
  type: SparqlTokenType.LITERAL;
}
export interface SparqlVarToken extends BaseSparqlToken {
  var: IToken;
  type: SparqlTokenType.VAR;
}

export type SparqlToken = SparqlIriToken | SparqlLiteralToken | SparqlVarToken;

export const enum SparqlResultType {
  TRIPLE = 'TRIPLE',
  FILTER = 'FILTER',
}

interface BaseSparqlResult {
  type: SparqlResultType;
}

export interface SparqlTripleResult extends BaseSparqlResult {
  subject: SparqlToken;
  predicate: IToken;
  object: SparqlToken;
  type: SparqlResultType.TRIPLE;
}

export interface SparqlFilter {
  subject: SparqlToken;
  subjectFunction?: IToken;
  operator: IToken;
  operatorBang?: IToken;
  value: SparqlToken;
  valueFunction?: IToken;
}

export interface SparqlFilterResult extends BaseSparqlResult {
  filters: SparqlFilter[];
  type: SparqlResultType.FILTER;
}

export type SparqlResult = SparqlTripleResult | SparqlFilterResult;

const parser = new StardogSparqlParser();

const processSparqlToken = (
  chainableCst: ChainableCst<ChainableSparqlCst>,
  token: SparqlToken
) => {
  switch (token.type) {
    case SparqlTokenType.IRI:
      chainableCst.processTokens(token.iri);
      break;
    case SparqlTokenType.LITERAL:
      chainableCst.processTokens(token.literal, token.langIri);
      break;
    case SparqlTokenType.VAR:
      chainableCst.processTokens(token.var);
      break;
    // istanbul ignore next
    default:
      break;
  }
};

const processVarParent = (
  chainableCst: ChainableCst<ChainableSparqlCst>,
  parent: CstNode | undefined
): SparqlVarToken | undefined => {
  const varToken =
    chainableCst
      .newChainableCst(parent)
      .getFirstCstNode('Var')
      .getFirstToken(sparqlTokens.sparqlTokenMap.VAR1.name) ||
    chainableCst
      .newChainableCst(parent)
      .getFirstCstNode('Var')
      .getFirstToken(sparqlTokens.sparqlTokenMap.VAR2.name);
  return varToken
    ? { var: varToken, type: SparqlTokenType.VAR }
    : /* istanbul ignore next */ undefined;
};

const processIriParent = (
  chainableCst: ChainableCst<ChainableSparqlCst>,
  parent: CstNode | undefined
): IToken | undefined => {
  return (
    chainableCst
      .newChainableCst(parent)
      .getFirstCstNode('iri')
      .getFirstToken(sparqlTokens.sparqlTokenMap.IRIREF.name) ||
    chainableCst
      .newChainableCst(parent)
      .getFirstCstNode('iri')
      .getFirstCstNode('PrefixedName')
      .getFirstToken(sparqlTokens.sparqlTokenMap.PNAME_LN.name) ||
    chainableCst
      .newChainableCst(parent)
      .getFirstCstNode('iri')
      .getFirstCstNode('PrefixedName')
      .getFirstToken(sparqlTokens.sparqlTokenMap.PNAME_NS.name)
  );
};

const processLiteralParent = (
  chainableCst: ChainableCst<ChainableSparqlCst>,
  parent: CstNode | undefined
): SparqlLiteralToken | undefined => {
  const rdfLiteral = chainableCst
    .newChainableCst(parent)
    .getFirstCstNode('RDFLiteral').current;
  if (rdfLiteral) {
    const stringCst = chainableCst
      .newChainableCst(rdfLiteral)
      .getFirstCstNode('String');
    const literal =
      stringCst.getFirstToken(
        sparqlTokens.sparqlTokenMap.STRING_LITERAL1.name
      ) ||
      stringCst.getFirstToken(
        sparqlTokens.sparqlTokenMap.STRING_LITERAL2.name
      ) ||
      stringCst.getFirstToken(
        sparqlTokens.sparqlTokenMap.STRING_LITERAL_LONG1.name
      ) ||
      stringCst.getFirstToken(
        sparqlTokens.sparqlTokenMap.STRING_LITERAL_LONG2.name
      );

    const langIri = processIriParent(chainableCst, rdfLiteral);
    return literal
      ? { literal, langIri, type: SparqlTokenType.LITERAL }
      : /* istanbul ignore next */ undefined;
  }

  const numericLiteral = chainableCst
    .newChainableCst(parent)
    .getFirstCstNode('NumericLiteral').current;
  if (numericLiteral) {
    const numericLiteralUnsigned = chainableCst
      .newChainableCst(numericLiteral)
      .getFirstCstNode('NumericLiteralUnsigned');
    if (numericLiteralUnsigned.current) {
      const literal =
        numericLiteralUnsigned.getFirstToken(
          sparqlTokens.sparqlTokenMap.INTEGER.name
        ) ||
        numericLiteralUnsigned.getFirstToken(
          sparqlTokens.sparqlTokenMap.DECIMAL.name
        ) ||
        numericLiteralUnsigned.getFirstToken(
          sparqlTokens.sparqlTokenMap.DOUBLE.name
        );
      return literal
        ? { literal, type: SparqlTokenType.LITERAL }
        : /* istanbul ignore next */ undefined;
    }

    const numericLiteralPositive = chainableCst
      .newChainableCst(numericLiteral)
      .getFirstCstNode('NumericLiteralPositive');
    if (numericLiteralPositive.current) {
      const literal =
        numericLiteralPositive.getFirstToken(
          sparqlTokens.sparqlTokenMap.INTEGER_POSITIVE.name
        ) ||
        numericLiteralPositive.getFirstToken(
          sparqlTokens.sparqlTokenMap.DECIMAL_POSITIVE.name
        ) ||
        numericLiteralPositive.getFirstToken(
          sparqlTokens.sparqlTokenMap.DOUBLE_POSITIVE.name
        );
      return literal
        ? { literal, type: SparqlTokenType.LITERAL }
        : /* istanbul ignore next */ undefined;
    }

    const numericLiteralNegative = chainableCst
      .newChainableCst(numericLiteral)
      .getFirstCstNode('NumericLiteralNegative');
    if (numericLiteralNegative.current) {
      const literal =
        numericLiteralNegative.getFirstToken(
          sparqlTokens.sparqlTokenMap.INTEGER_NEGATIVE.name
        ) ||
        numericLiteralNegative.getFirstToken(
          sparqlTokens.sparqlTokenMap.DECIMAL_NEGATIVE.name
        ) ||
        numericLiteralNegative.getFirstToken(
          sparqlTokens.sparqlTokenMap.DOUBLE_NEGATIVE.name
        );
      return literal
        ? { literal, type: SparqlTokenType.LITERAL }
        : /* istanbul ignore next */ undefined;
    }
  }

  const booleanLiteral = chainableCst
    .newChainableCst(parent)
    .getFirstCstNode('BooleanLiteral');
  if (booleanLiteral.current) {
    const literal =
      booleanLiteral.getFirstToken(sparqlTokens.sparqlTokenMap.TRUE.name) ||
      booleanLiteral.getFirstToken(sparqlTokens.sparqlTokenMap.FALSE.name);
    return literal
      ? { literal, type: SparqlTokenType.LITERAL }
      : /* istanbul ignore next */ undefined;
  }

  return undefined;
};

const processGraphTermParent = (
  chainableCst: ChainableCst<ChainableSparqlCst>,
  parent: CstNode | undefined,
  literal = false
): SparqlToken | undefined => {
  const graphTerm = chainableCst
    .newChainableCst(parent)
    .getFirstCstNode('GraphTerm').current;
  const iri = processIriParent(chainableCst, graphTerm);
  if (iri) {
    return { iri, type: SparqlTokenType.IRI };
  }

  if (literal) {
    return processLiteralParent(chainableCst, graphTerm);
  }

  return undefined;
};

const processPropertyListNotEmptyParent = (
  chainableCst: ChainableCst<ChainableSparqlCst>,
  parent: CstNode | undefined,
  subject: SparqlToken,
  results: SparqlResult[]
) => {
  const propertyList = chainableCst
    .newChainableCst(parent)
    .getFirstCstNode('PropertyListNotEmpty').current;
  if (!propertyList) {
    return;
  }

  const groups = groupMany<StardogSparqlParser>(propertyList, [
    ['Verb', 'ObjectList'],
  ]);
  groups.forEach((group) => {
    const predicate =
      processIriParent(
        chainableCst,
        chainableCst
          .newChainableCst(group)
          .getFirstCstNode('Verb')
          .getFirstCstNode('VarOrIri').current
      ) ||
      chainableCst
        .newChainableCst(group)
        .getFirstCstNode('Verb')
        .getFirstToken(sparqlTokens.sparqlTokenMap.A.name);
    if (!predicate) {
      return;
    }

    const objects = chainableCst
      .newChainableCst(group)
      .getFirstCstNode('ObjectList')
      .getCstElements('Object');
    // istanbul ignore if
    if (!objects) {
      return;
    }

    objects.forEach((objectElement) => {
      if (isCstNode(objectElement)) {
        const varOrTerm = chainableCst
          .newChainableCst(objectElement)
          .getFirstCstNode('GraphNode')
          .getFirstCstNode('VarOrTerm').current;
        const object =
          processVarParent(chainableCst, varOrTerm) ||
          processGraphTermParent(chainableCst, varOrTerm);

        if (object) {
          processSparqlToken(chainableCst, subject);
          chainableCst.processTokens(predicate);
          processSparqlToken(chainableCst, object);
          results.push({
            subject,
            predicate,
            object,
            type: SparqlResultType.TRIPLE,
          });
        }
      }
    });
  });
};

const processPathSequence = (
  chainableCst: ChainableCst<ChainableSparqlCst>,
  pathSequence: CstNode | undefined
) => {
  const pathEltOrInverses =
    pathSequence &&
    chainableCst
      .newChainableCst(pathSequence)
      .getCstElements('PathEltOrInverse');

  const pathEltOrInverse =
    pathSequence &&
    pathEltOrInverses &&
    pathEltOrInverses.length &&
    chainableCst
      .newChainableCst(pathSequence)
      .getFirstCstNode('PathEltOrInverse').current;

  const caret =
    pathEltOrInverse &&
    chainableCst
      .newChainableCst(pathEltOrInverse)
      .getFirstToken(sparqlTokens.sparqlTokenMap.Caret.name);

  const pathElt =
    pathEltOrInverse &&
    !caret &&
    chainableCst.newChainableCst(pathEltOrInverse).getFirstCstNode('PathElt')
      .current;

  const pathPrimary =
    pathElt &&
    !chainableCst.newChainableCst(pathElt).getCstElements('PathMod')?.length &&
    chainableCst.newChainableCst(pathElt).getFirstCstNode('PathPrimary');

  return (
    (pathPrimary &&
      (processIriParent(chainableCst, pathPrimary.current) ||
        pathPrimary.getFirstToken(sparqlTokens.sparqlTokenMap.A.name))) ||
    null
  );
};

const processPropertyListPathNotEmptyParent = (
  chainableCst: ChainableCst<ChainableSparqlCst>,
  parent: CstNode | undefined,
  subject: SparqlToken,
  results: SparqlResult[]
) => {
  const propertyList = chainableCst
    .newChainableCst(parent)
    .getFirstCstNode('PropertyListPathNotEmpty').current;
  if (!propertyList) {
    return;
  }

  const groups = groupMany<StardogSparqlParser>(propertyList, [
    ['VerbPath', 'ObjectListPath'],
    ['VerbSimple', 'ObjectListPath'],
  ]);
  groups.forEach((group) => {
    const pathAlternative = chainableCst
      .newChainableCst(group)
      .getFirstCstNode('VerbPath')
      .getFirstCstNode('Path')
      .getFirstCstNode('PathAlternative').current;

    const predicates: IToken[] = [];

    chainableCst
      .newChainableCst(pathAlternative)
      .getCstElements('PathSequence')
      ?.forEach((pathSequence) => {
        if (isCstNode(pathSequence)) {
          const prediate = processPathSequence(chainableCst, pathSequence);
          if (prediate) {
            predicates.push(prediate);
          }
        }
      });

    let predicate: IToken | undefined;
    if (predicates.length === 1) {
      [predicate] = predicates;
    } else if (!predicates.length) {
      predicate = processVarParent(
        chainableCst,
        chainableCst.newChainableCst(group).getFirstCstNode('VerbSimple')
          .current
      )?.var;
    } else {
      predicate = predicates.find(
        (token) =>
          getFullIri(token, chainableCst.namespaces) === STARDOG_LABEL_IRI
      );
    }

    if (!predicate) {
      return;
    }

    const objectPaths = chainableCst
      .newChainableCst(group)
      .getFirstCstNode('ObjectListPath')
      .getCstElements('ObjectPath');
    // istanbul ignore if
    if (!objectPaths) {
      return;
    }

    objectPaths.forEach((objectPath) => {
      if (isCstNode(objectPath)) {
        const varOrTermOrEtp = chainableCst
          .newChainableCst(objectPath)
          .getFirstCstNode('GraphNodePath')
          .getFirstCstNode('VarOrTermOrEmbeddedTriplePattern').current;
        const object =
          processVarParent(chainableCst, varOrTermOrEtp) ||
          processGraphTermParent(chainableCst, varOrTermOrEtp, true);

        if (object) {
          processSparqlToken(chainableCst, subject);
          chainableCst.processTokens(predicate, ...predicates);
          processSparqlToken(chainableCst, object);
          results.push({
            subject,
            predicate: predicate as IToken,
            object,
            type: SparqlResultType.TRIPLE,
          });
        }
      }
    });
  });
};

const processTripleParent = (
  chainableCst: ChainableCst<ChainableSparqlCst>,
  parent: CstNode | undefined,
  results: SparqlResult[]
) => {
  const varOrTerm = chainableCst
    .newChainableCst(parent)
    .getFirstCstNode('VarOrTerm').current;
  const subject =
    processVarParent(chainableCst, varOrTerm) ||
    processGraphTermParent(chainableCst, varOrTerm);
  if (!subject) {
    return;
  }

  processPropertyListNotEmptyParent(chainableCst, parent, subject, results);
  processPropertyListPathNotEmptyParent(chainableCst, parent, subject, results);
};

export const processTriplesBlock = (
  chainableCst: ChainableCst<ChainableSparqlCst>,
  triplesBlock: CstNode | undefined,
  results: SparqlResult[]
) => {
  const triples = chainableCst
    .newChainableCst(triplesBlock)
    .getFirstCstNode('TriplesSameSubjectPath');
  processTripleParent(chainableCst, triples.current, results);

  processTriplesBlockParent(chainableCst, triplesBlock, results);
};

const processTriplesBlockParent = (
  chainableCst: ChainableCst<ChainableSparqlCst>,
  parent: CstNode | undefined,
  results: SparqlResult[]
) => {
  const triplesBlocks = chainableCst
    .newChainableCst(parent)
    .getCstElements('TriplesBlock');
  if (!triplesBlocks) {
    return;
  }

  triplesBlocks.forEach((triplesBlock) => {
    if (isCstNode(triplesBlock)) {
      processTriplesBlock(chainableCst, triplesBlock, results);
    }
  });
};

const processTriplesTemplate = (
  chainableCst: ChainableCst<ChainableSparqlCst>,
  triplesTemplate: CstNode | undefined,
  results: SparqlResult[]
) => {
  const triples = chainableCst
    .newChainableCst(triplesTemplate)
    .getFirstCstNode('TriplesSameSubject');
  processTripleParent(chainableCst, triples.current, results);

  processTriplesTemplateParent(chainableCst, triplesTemplate, results);
};

const processTriplesTemplateParent = (
  chainableCst: ChainableCst<ChainableSparqlCst>,
  parent: CstNode | undefined,
  results: SparqlResult[]
) => {
  const triplesTemplates = chainableCst
    .newChainableCst(parent)
    .getCstElements('TriplesTemplate');
  if (!triplesTemplates) {
    return;
  }

  triplesTemplates.forEach((triplesTemplate) => {
    if (isCstNode(triplesTemplate)) {
      processTriplesTemplate(chainableCst, triplesTemplate, results);
    }
  });
};

const processBuiltInCallExpression = (
  chainableCst: ChainableCst<ChainableSparqlCst>,
  parent: CstNode | undefined,
  { processStr }: { processStr: boolean }
): {
  token: SparqlToken;
  tokenFunction?: IToken;
} | null => {
  const condOrExp = chainableCst
    .newChainableCst(parent)
    .getFirstCstNode('ConditionalOrExpression').current;

  const condAndExps = chainableCst
    .newChainableCst(condOrExp)
    .getCstElements('ConditionalAndExpression');
  if (!condAndExps || condAndExps.length !== 1) {
    return null;
  }

  const valueLogicals = chainableCst
    .newChainableCst(condOrExp)
    .getFirstCstNode('ConditionalAndExpression')
    .getCstElements('ValueLogical');
  if (!valueLogicals || valueLogicals.length !== 1) {
    return null;
  }

  const relExpression = chainableCst
    .newChainableCst(condOrExp)
    .getFirstCstNode('ConditionalAndExpression')
    .getFirstCstNode('ValueLogical')
    .getFirstCstNode('RelationalExpression').current;

  const numExpressions = chainableCst
    .newChainableCst(relExpression)
    .getCstElements('NumericExpression');
  if (!numExpressions || numExpressions.length !== 1) {
    return null;
  }

  const unaryExpression = chainableCst
    .newChainableCst(relExpression)
    .getFirstCstNode('NumericExpression')
    .getFirstCstNode('AdditiveExpression')
    .getFirstCstNode('MultiplicativeExpression')
    .getFirstCstNode('UnaryExpression').current;

  if (
    chainableCst
      .newChainableCst(unaryExpression)
      .getFirstToken(sparqlTokens.sparqlTokenMap.Bang.name)
  ) {
    return null;
  }

  const primaryExpression = chainableCst
    .newChainableCst(unaryExpression)
    .getFirstCstNode('PrimaryExpression').current;

  const expression = chainableCst
    .newChainableCst(primaryExpression)
    .getFirstCstNode('BrackettedExpression')
    .getFirstCstNode('Expression').current;
  if (expression) {
    return processBuiltInCallExpression(chainableCst, expression, {
      processStr: false,
    });
  }

  const str = chainableCst
    .newChainableCst(primaryExpression)
    .getFirstCstNode('BuiltInCall')
    .getFirstCstNode('BuiltInCall_STR').current;
  if (processStr && str) {
    const strToken = processBuiltInCallExpression(
      chainableCst,
      chainableCst.newChainableCst(str).getFirstCstNode('Expression').current,
      { processStr: false }
    );
    if (strToken) {
      return {
        token: strToken.token,
        tokenFunction: chainableCst
          .newChainableCst(str)
          .getFirstToken(sparqlTokens.sparqlTokenMap.STR.name),
      };
    }
  }

  const varOrLiteral =
    processVarParent(chainableCst, primaryExpression) ||
    processLiteralParent(chainableCst, primaryExpression);
  if (varOrLiteral) {
    return { token: varOrLiteral };
  }

  const iriOrFunction = chainableCst
    .newChainableCst(primaryExpression)
    .getFirstCstNode('iriOrFunction').current;
  if (
    !chainableCst.newChainableCst(iriOrFunction).getFirstCstNode('ArgList')
      .current
  ) {
    const iri = processIriParent(chainableCst, iriOrFunction);
    if (iri) {
      return { token: { iri, type: SparqlTokenType.IRI } };
    }
  }

  return null;
};

const processBracketedExpressionParent = (
  chainableCst: ChainableCst<ChainableSparqlCst>,
  parent: CstNode | undefined
) => {
  const filters: SparqlFilter[] = [];
  const condOrExp = chainableCst
    .newChainableCst(parent)
    .getFirstCstNode('BrackettedExpression')
    .getFirstCstNode('Expression')
    .getFirstCstNode('ConditionalOrExpression').current;

  const condAndExps = chainableCst
    .newChainableCst(condOrExp)
    .getCstElements('ConditionalAndExpression');
  if (!condAndExps || condAndExps.length !== 1) {
    return filters;
  }

  const valueLogicals = chainableCst
    .newChainableCst(condOrExp)
    .getFirstCstNode('ConditionalAndExpression')
    .getCstElements('ValueLogical');
  // istanbul ignore if
  if (!valueLogicals) {
    return filters;
  }

  valueLogicals.forEach((valueLogical) => {
    if (isCstNode(valueLogical)) {
      const relExpression = chainableCst
        .newChainableCst(valueLogical)
        .getFirstCstNode('RelationalExpression');

      const numExpressions = relExpression.getCstElements('NumericExpression');
      // istanbul ignore if
      if (!numExpressions) {
        return;
      }

      let notContainsToken: IToken | undefined;
      let containsToken: IToken | undefined;
      const values: Array<{ token: SparqlToken; tokenFunction?: IToken }> = [];

      numExpressions.forEach((numExpression) => {
        if (isCstNode(numExpression)) {
          const unaryExpression = chainableCst
            .newChainableCst(numExpression)
            .getFirstCstNode('AdditiveExpression')
            .getFirstCstNode('MultiplicativeExpression')
            .getFirstCstNode('UnaryExpression').current;

          const primaryExpression = chainableCst
            .newChainableCst(unaryExpression)
            .getFirstCstNode('PrimaryExpression').current;

          const bangToken = chainableCst
            .newChainableCst(unaryExpression)
            .getFirstToken(sparqlTokens.sparqlTokenMap.Bang.name);

          if (
            !bangToken &&
            numExpressions.length === 1 &&
            chainableCst
              .newChainableCst(primaryExpression)
              .getFirstCstNode('BrackettedExpression').current
          ) {
            const nestedFilters = processBracketedExpressionParent(
              chainableCst,
              primaryExpression
            );
            filters.push(...nestedFilters);
            return;
          }

          const str = chainableCst
            .newChainableCst(primaryExpression)
            .getFirstCstNode('BuiltInCall')
            .getFirstCstNode('BuiltInCall_STR').current;

          if (!bangToken && str) {
            const strToken = processBuiltInCallExpression(
              chainableCst,
              chainableCst.newChainableCst(str).getFirstCstNode('Expression')
                .current,
              { processStr: false }
            );
            if (strToken) {
              values.push({
                token: strToken.token,
                tokenFunction: chainableCst
                  .newChainableCst(str)
                  .getFirstToken(sparqlTokens.sparqlTokenMap.STR.name),
              });
            }
            return;
          }

          const contains = chainableCst
            .newChainableCst(primaryExpression)
            .getFirstCstNode('BuiltInCall')
            .getFirstCstNode('BuiltInCall_CONTAINS').current;
          if (contains) {
            notContainsToken = bangToken;
            containsToken = chainableCst
              .newChainableCst(contains)
              .getFirstToken(sparqlTokens.sparqlTokenMap.CONTAINS.name);
            containsToken = chainableCst
              .newChainableCst(contains)
              .getFirstToken(sparqlTokens.sparqlTokenMap.CONTAINS.name);
            const expressions = chainableCst
              .newChainableCst(contains)
              .getCstElements('Expression');
            if (expressions) {
              const [firstExpression, secondExpression] = expressions;
              if (isCstNode(firstExpression) && isCstNode(secondExpression)) {
                const firstToken = processBuiltInCallExpression(
                  chainableCst,
                  firstExpression,
                  { processStr: true }
                );
                const secondToken = processBuiltInCallExpression(
                  chainableCst,
                  secondExpression,
                  { processStr: true }
                );
                if (firstToken && secondToken) {
                  values.push(firstToken, secondToken);
                }
                return;
              }
            }
          }

          if (!bangToken) {
            const varOrLiteral =
              processVarParent(chainableCst, primaryExpression) ||
              processLiteralParent(chainableCst, primaryExpression);
            if (varOrLiteral) {
              values.push({ token: varOrLiteral });
              return;
            }

            const iriOrFunction = chainableCst
              .newChainableCst(primaryExpression)
              .getFirstCstNode('iriOrFunction').current;
            if (
              !chainableCst
                .newChainableCst(iriOrFunction)
                .getFirstCstNode('ArgList').current
            ) {
              const iri = processIriParent(chainableCst, iriOrFunction);
              if (iri) {
                values.push({ token: { iri, type: SparqlTokenType.IRI } });
              }
            }
          }
        }
      });

      if (containsToken && values.length === 2) {
        processSparqlToken(chainableCst, values[0].token);
        processSparqlToken(chainableCst, values[1].token);
        chainableCst.processTokens(
          containsToken,
          values[0].tokenFunction,
          values[1].tokenFunction
        );
        filters.push({
          subject: values[0].token,
          subjectFunction: values[0].tokenFunction,
          operator: containsToken,
          operatorBang: notContainsToken,
          value: values[1].token,
          valueFunction: values[1].tokenFunction,
        });
      }

      const operator =
        relExpression.getFirstToken(sparqlTokens.sparqlTokenMap.Equals.name) ||
        relExpression.getFirstToken(
          sparqlTokens.sparqlTokenMap.NotEquals.name
        ) ||
        relExpression.getFirstToken(
          sparqlTokens.sparqlTokenMap.LessThan.name
        ) ||
        relExpression.getFirstToken(
          sparqlTokens.sparqlTokenMap.GreaterThan.name
        ) ||
        relExpression.getFirstToken(
          sparqlTokens.sparqlTokenMap.LessThanEquals.name
        ) ||
        relExpression.getFirstToken(
          sparqlTokens.sparqlTokenMap.GreaterThanEquals.name
        );

      if (operator && values.length === 2) {
        processSparqlToken(chainableCst, values[0].token);
        processSparqlToken(chainableCst, values[1].token);
        chainableCst.processTokens(
          values[0].tokenFunction,
          values[1].tokenFunction
        );
        filters.push({
          subject: values[0].token,
          subjectFunction: values[0].tokenFunction,
          operator,
          value: values[1].token,
          valueFunction: values[1].tokenFunction,
        });
      }
    }
  });

  return filters;
};

const processFilterParent = (
  chainableCst: ChainableCst<ChainableSparqlCst>,
  parent: CstNode | undefined,
  results: SparqlResult[]
) => {
  const filters = processBracketedExpressionParent(
    chainableCst,
    chainableCst
      .newChainableCst(parent)
      .getFirstCstNode('Filter')
      .getFirstCstNode('Constraint').current
  );

  if (filters.length) {
    results.push({ filters, type: SparqlResultType.FILTER });
  }
};

const processGroupGraphPatternSub = (
  chainableCst: ChainableCst<ChainableSparqlCst>,
  ggpSub: CstNode | undefined,
  results: SparqlResult[],
  {
    processOptional,
    processUnion,
  }: {
    processOptional: boolean;
    processUnion: boolean;
  }
) => {
  processTriplesBlockParent(chainableCst, ggpSub, results);

  chainableCst
    .newChainableCst(ggpSub)
    .getCstElements('GraphPatternNotTriples')
    ?.forEach((gpnt) => {
      if (isCstNode(gpnt)) {
        const ggps = chainableCst
          .newChainableCst(gpnt)
          .getFirstCstNode('GroupOrUnionGraphPattern')
          .getCstElements('GroupGraphPattern');

        if (!processUnion && ggps?.length === 1) {
          if (isCstNode(ggps[0])) {
            processGroupGraphPattern(chainableCst, ggps[0], results, {
              processOptional,
              processUnion,
            });
          }
        } else if (processUnion) {
          ggps?.forEach((ggp) => {
            if (isCstNode(ggp)) {
              processGroupGraphPattern(chainableCst, ggp, results, {
                processOptional,
                processUnion,
              });
            }
          });
        }

        const optional = chainableCst
          .newChainableCst(gpnt)
          .getFirstCstNode('OptionalGraphPattern')
          .getFirstCstNode('GroupGraphPattern').current;
        if (processOptional && optional) {
          processGroupGraphPattern(chainableCst, optional, results, {
            processOptional,
            processUnion,
          });
        }

        processFilterParent(chainableCst, gpnt, results);
      }
    });
};

export const processGroupGraphPattern = (
  chainableCst: ChainableCst<ChainableSparqlCst>,
  ggp: CstNode | undefined,
  results: SparqlResult[],
  {
    processOptional,
    processUnion,
  }: {
    processOptional: boolean;
    processUnion: boolean;
  }
) => {
  const subSelect = chainableCst
    .newChainableCst(ggp)
    .getFirstCstNode('SubSelect')
    .getFirstCstNode('WhereClause')
    .getFirstCstNode('GroupGraphPattern').current;
  if (subSelect) {
    processGroupGraphPattern(chainableCst, subSelect, results, {
      processOptional,
      processUnion,
    });
  }

  const ggpSub = chainableCst
    .newChainableCst(ggp)
    .getFirstCstNode('GroupGraphPatternSub');
  processGroupGraphPatternSub(chainableCst, ggpSub.current, results, {
    processOptional,
    processUnion,
  });

  return results;
};

export const sparqlTriples = (
  query: string,
  initialNamespaces: Record<string, string>
): {
  namespaces: Record<string, string>;
  tokens: { all: IToken[]; ignored: IToken[]; unprocessed: IToken[] };
  triples: SparqlResult[];
} | null => {
  const { cst, errors } = parser.parse(query.trim());

  if (errors.length) {
    console.warn(errors);
    return null;
  }
  const namespaces: Record<string, string> = { ...initialNamespaces };
  const triples: SparqlResult[] = [];

  const chainableCst = new ChainableCst<ChainableSparqlCst>(
    ChainableSparqlCst,
    namespaces
  );

  chainableCst
    .newChainableCst(cst)
    .getFirstCstNode('Prologue')
    .getCstElements('PrefixDecl')
    ?.forEach((cstElement) => {
      if (isCstNode(cstElement)) {
        const prefixDecl = chainableCst.newChainableCst(cstElement);
        const pnameNs = prefixDecl.getFirstToken(
          sparqlTokens.sparqlTokenMap.PNAME_NS.name
        );
        const iriRef = prefixDecl.getFirstToken(
          sparqlTokens.sparqlTokenMap.IRIREF.name
        );
        if (pnameNs && iriRef) {
          const prefix = pnameNs.image.slice(0, pnameNs.image.length - 1);
          const iri = iriRef.image.slice(1, iriRef.image.length - 1);
          namespaces[prefix] = iri;
        }
      }
    });

  let cstNode: CstNode | undefined;

  const selectGGP = chainableCst
    .newChainableCst(cst)
    .getFirstCstNode('QueryUnit')
    .getFirstCstNode('Query')
    .getFirstCstNode('SelectQuery')
    .getFirstCstNode('WhereClause')
    .getFirstCstNode('GroupGraphPattern').current;
  if (selectGGP) {
    cstNode = selectGGP;
    processGroupGraphPattern(chainableCst, selectGGP, triples, {
      processOptional: true,
      processUnion: true,
    });
  }

  const constructGGP = chainableCst
    .newChainableCst(cst)
    .getFirstCstNode('QueryUnit')
    .getFirstCstNode('Query')
    .getFirstCstNode('ConstructQuery')
    .getFirstCstNode('WhereClause')
    .getFirstCstNode('GroupGraphPattern').current;
  if (constructGGP) {
    cstNode = constructGGP;
    processGroupGraphPattern(chainableCst, constructGGP, triples, {
      processOptional: true,
      processUnion: true,
    });
  }

  const constructQuery = chainableCst
    .newChainableCst(cst)
    .getFirstCstNode('QueryUnit')
    .getFirstCstNode('Query')
    .getFirstCstNode('ConstructQuery').current;
  if (constructQuery) {
    cstNode = constructQuery;
    processTriplesTemplateParent(chainableCst, constructQuery, triples);
  }

  const { allTokens } = chainableCst.getTokens(cst);
  const {
    allTokens: allQueryTokens,
    unprocessedTokens: unprocessedQueryTokens,
  } = chainableCst.getTokens(cstNode);

  return {
    namespaces,
    tokens: {
      all: Array.from(allTokens).sort(tokenSort),
      ignored: Array.from(allTokens)
        .filter((token) => !allQueryTokens.has(token))
        .sort(tokenSort),
      unprocessed: Array.from(unprocessedQueryTokens).sort(tokenSort),
    },
    triples,
  };
};
